import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Form } from "antd";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { loginSettingsData } from "Pages/Login/Stores/LoginSettingsStore";
import { withTranslation, WithTranslation } from "react-i18next";
const FormItem = Form.Item;

export function checkPasswordComplexity(value: string, passwordDefinitions: API_PasswordPolicy) {
  const complexityClass = passwordDefinitions.complexityClass || 1;
  const minLength = passwordDefinitions.minimumLength || 6;
  const passwordStrengthRequirements = [
    {
      pattern: /[a-zäöüß]/,
      message: "Contain small letters.",
    },
    {
      pattern: /[A-ZÄÖÜ]/,
      message: "Contain capital letters.",
    },
    {
      pattern: /[0-9]/,
      message: "Contain numbers.",
    },
    {
      // eslint-disable-next-line no-useless-escape
      pattern: /[\!\"\#\$\%\&\'\(\)\*\+\,\-\.\/\:\;\<\=\>\?\@\[\\\]\^\_\`\{\|\}\~]/,
      message: "Contain special characters.",
    },
  ];
  return {
    isValid:
      passwordStrengthRequirements.filter((i) => value.match(i.pattern)).length >= complexityClass &&
      value.length >= minLength,
    passwordStrengthRequirements,
    minLength: {
      min: minLength,
      message: "Password must be at least 8 characters",
    },
  };
}

interface PasswordComplexityProps extends WithTranslation {
  inputValue: string;
}

export const PasswordComplexity = withTranslation(I18N_NS.LOGIN)(
  observer((props: PasswordComplexityProps) => {
    const { inputValue, t } = props;
    const { passwordPolicy } = loginSettingsData;
    const { isValid, passwordStrengthRequirements } = checkPasswordComplexity(inputValue, passwordPolicy);
    loginSettingsData.setIsValid(isValid);
    const warningIcon = <FontAwesomeIcon icon={["fas", "times"]} fixedWidth={true} className="text-warning" />;
    const successIcon = <FontAwesomeIcon icon={["fas", "check"]} fixedWidth={true} className="text-success" />;
    const requirementsList = (
      <div>
        {passwordStrengthRequirements.map((i, idx) => {
          const icon = !inputValue.match(i.pattern) ? warningIcon : successIcon;
          return (
            <div key={idx}>
              {icon} {t(i.message)}
            </div>
          );
        })}
      </div>
    );

    return (
      <FormItem>
        <Alert
          type={isValid ? "success" : "warning"}
          style={{ padding: 8 }}
          title={
            <div style={{ fontSize: "0.8rem" }}>
              {isValid ? (
                <p style={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                  {t("Your password meets the security requirements.")}
                </p>
              ) : null}
              <p>
                {inputValue.length >= 8 ? successIcon : warningIcon}
                {t("The password must have at least {{minimumLength}} characters.", {
                  minimumLength: passwordPolicy.minimumLength,
                })}
              </p>
              <p>
                {t(
                  `The password must contain {{complexityClass}} classes out of the four available character classes. You can combine any {{complexityClass}} of these.`,
                  { complexityClass: passwordPolicy.complexityClass }
                )}
              </p>
              {requirementsList}
            </div>
          }
        />
      </FormItem>
    );
  })
);
